import {
  computeIsSelectionOnlyDatetimeCells,
  computeLinkButtonDisabled,
  computeSelectionAlignment,
  computeSelectionLink,
  computeSelectionPrecision,
  SelectionLink,
} from 'notebook/tables/store/logic/TableActiveCellLogic';
import {TableSelectionModel} from 'notebook/tables/store/selectors/tableModel/TableSelectionSelectors';
import {createTableModelSelector} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {TableEditableStore, TableEditableVolatileProps} from 'notebook/tables/store/TableStore';
import {AlignmentState} from 'notebook/tables/toolbar/FormulaBox';
import {Optional} from 'util/Types';

/**
 * Model containing formula-bar / table-controls state for the currently active
 * cell.
 *
 * TODO(BNCH-146929): The following active-cell formatting properties are added in
 * follow-up slices as logic is transitioned out of TableEditable:
 *   - buttonFormatting: {active: boolean; disabled: boolean}; // From _mergeButtonFormatting()
 */
export interface TableActiveCellModel {
  /**
   * Whether the "add link" button is disabled. A link may only be set when
   * exactly one cell is selected.
   */
  linkButtonDisabled: boolean;

  /**
   * The link (url + text) of the active cell, or an empty object when there is
   * no linkable selection.
   */
  link: SelectionLink;

  /**
   * The shared precision across all rendered selected cells, or `null` when
   * there is no selection or the selected cells have differing precisions.
   */
  precision: Optional<number>;

  /**
   * The alignment state (explicit `active` alignment and rendered `default`
   * alignment) for the current selection.
   */
  alignment: AlignmentState;

  /**
   * Whether the current selection contains only datetime cells. Used to gate
   * the "insert now" datetime action in the table controls.
   */
  isDatetimeCell: boolean;
}

/**
 * Creates a memoized selector that computes the TableActiveCellModel from the store.
 */
export function createTableActiveCellSelector(
  volatilePropsSelector: (store: TableEditableStore) => TableEditableVolatileProps,
  tableSelectionSelector: (store: TableEditableStore) => TableSelectionModel
): (store: TableEditableStore) => TableActiveCellModel {
  return createTableModelSelector(
    [
      (store) => store.state.multiStartIndex,
      (store) => store.state.multiEndIndex,
      (store) => volatilePropsSelector(store).table,
      (store) => volatilePropsSelector(store).computedTable,
      (store) => tableSelectionSelector(store).hasRealSelection,
      (store) => tableSelectionSelector(store).expandedBox,
    ],
    (multiStartIndex, multiEndIndex, table, computedTable, hasRealSelection, expandedBox) => {
      const linkButtonDisabled = computeLinkButtonDisabled(multiStartIndex, multiEndIndex);
      return {
        linkButtonDisabled,
        link: computeSelectionLink(hasRealSelection, linkButtonDisabled, multiStartIndex, table),
        precision: computeSelectionPrecision(table, computedTable, expandedBox, hasRealSelection),
        alignment: computeSelectionAlignment(table, computedTable, expandedBox, hasRealSelection),
        isDatetimeCell: computeIsSelectionOnlyDatetimeCells(
          table.columnConstraints,
          expandedBox,
          hasRealSelection
        ),
      };
    }
  );
}
